import { z } from 'zod';
/**
 * Playbooks — narrative, AI-editable operating documents that capture how
 * a company runs (company-level) and how each department runs
 * (department-level). One company playbook per org; one playbook per
 * leadership area (engineering / sales / ops / marketing / cs / finance /
 * product). Newly-created orgs get a seeded set of eight defaults
 * (one company + seven departments) that
 * are immediately editable; the AI can refine them via the AI-edit
 * endpoint as the company evolves.
 *
 * Storage path: `playbooks/{playbookId}`. Reads gated by `orgId`. Sections
 * are embedded — they're ordered, edited together, and always read
 * alongside the parent playbook.
 *
 * This file lives at the package root (not under `eos/`) because
 * playbooks are a LeanOpx product surface, not a Wickman-EOS construct.
 * They sit alongside the (subset of) EOS artifacts we kept (Rocks +
 * Scorecard) but are conceptually a separate workstream.
 */
export const PLAYBOOKS_COLLECTION = 'playbooks';
/**
 * Playbook scope. `company` is the org-wide "how we operate" doc;
 * `department` is one playbook per leadership seat. Exactly one company
 * playbook per org; exactly one department playbook per (org,
 * departmentSlug).
 */
export const PlaybookScope = z.enum(['company', 'department']);
/**
 * Department slug. Closed enum so the seeded defaults always match a
 * known seat; expanding the leadership chart later means adding to this
 * enum (and the corresponding seed template).
 */
export const PlaybookDepartment = z.enum([
    'engineering',
    'sales',
    'operations',
    'marketing',
    'customer-success',
    'finance',
    'product',
]);
/**
 * Section type — drives a small icon/affordance in the UI and lets the
 * AI-edit pass reason about which sections are quantitative (`metric`)
 * vs. qualitative (`principle` / `process` / `cadence`).
 */
export const PlaybookSectionType = z.enum([
    'principle',
    'process',
    'metric',
    'cadence',
    'tool',
    'people',
]);
/**
 * A section inside a playbook. `body` is markdown — rendered in the
 * portal via the same markdown pipeline used for Insights / Briefings.
 * `id` is client-generated (uuid) so the array can be reordered/edited
 * without subcollection writes.
 */
export const PlaybookSection = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    body: z.string(),
    type: PlaybookSectionType,
    order: z.number().int().nonnegative(),
});
/**
 * The Playbook entity itself.
 *
 * Invariant (enforced via `.refine`): `departmentSlug` is null when
 * `scope === 'company'` and non-null when `scope === 'department'`. This
 * keeps the (orgId, scope, departmentSlug) tuple uniquely indexable in
 * Firestore without resorting to a separate "departmentSlug exists"
 * sentinel value.
 *
 * `generatedByAI` records that the *seeded defaults* came from the AI
 * suggestion pipeline (true on the eight defaults a new org receives);
 * `lastEditedByAI` flips true any time the AI-edit endpoint mutates the
 * doc and gives the UI a badge to render.
 */
export const Playbook = z
    .object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    scope: PlaybookScope,
    /** null when scope=company; the dept slug when scope=department. */
    departmentSlug: PlaybookDepartment.nullable(),
    title: z.string().min(1),
    summary: z.string(),
    sections: z.array(PlaybookSection).default([]),
    /** True for the eight AI-suggested defaults (1 company + 7 dept) seeded on org creation. */
    generatedByAI: z.boolean().default(false),
    /** Flips true any time the AI-edit endpoint mutates the doc. */
    lastEditedByAI: z.boolean().default(false),
    /** ISO timestamp of the most recent AI-edit pass; absent if never. */
    lastEditedByAIAt: z.string().optional(),
    /** Optional human owner — typically the seat-holder for the dept. */
    ownerUid: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    updatedByUid: z.string().min(1),
})
    .refine((p) => (p.scope === 'company' && p.departmentSlug === null) ||
    (p.scope === 'department' && p.departmentSlug !== null), {
    message: 'departmentSlug must be null when scope=company and set when scope=department',
});
/** Body for `POST /api/orgs/:o/playbooks`. */
export const CreatePlaybookRequest = z
    .object({
    scope: PlaybookScope,
    departmentSlug: PlaybookDepartment.nullable(),
    title: z.string().min(1),
    summary: z.string().default(''),
    sections: z.array(PlaybookSection).default([]),
    ownerUid: z.string().optional(),
})
    // Mirror the `Playbook` invariant on the create boundary: a server
    // that only validates against `CreatePlaybookRequest` would otherwise
    // accept e.g. `scope: 'company'` with a non-null `departmentSlug` and
    // write a doc that fails downstream `Playbook.parse`.
    .refine((p) => (p.scope === 'company' && p.departmentSlug === null) ||
    (p.scope === 'department' && p.departmentSlug !== null), {
    message: 'departmentSlug must be null when scope=company and set when scope=department',
});
/** Body for `PATCH /api/orgs/:o/playbooks/:id`. */
export const UpdatePlaybookRequest = z.object({
    title: z.string().min(1).optional(),
    summary: z.string().optional(),
    sections: z.array(PlaybookSection).optional(),
    /** Pass `null` to explicitly unset the owner. */
    ownerUid: z.string().nullable().optional(),
});
/**
 * Body for `POST /api/orgs/:o/playbooks/:id/ai-edit`.
 *
 * Phase 5 lands the real synthesis pass — until then the endpoint
 * returns a deterministic mock so the UI is testable end-to-end.
 *
 * `sectionId`: when provided, the AI is asked to revise that section
 * only; when omitted, the AI may revise the whole playbook.
 */
export const AIEditPlaybookRequest = z.object({
    prompt: z.string().min(1),
    sectionId: z.string().optional(),
});
/**
 * Response from the AI-edit endpoint. Carries the revised playbook + a
 * structured revision summary so the UI can show "AI rewrote 2 sections"
 * without diffing the doc.
 */
export const AIEditPlaybookResponse = z.object({
    playbook: Playbook,
    revision: z.object({
        summary: z.string(),
        changedSectionIds: z.array(z.string()),
        /** Model identifier — `mock-phase-5-stub` until Phase 5 lands. */
        model: z.string(),
    }),
});
